import moment from "moment";
import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { isLoadingNotifications, selectNotifications } from "../selectors.js";
import { fetchUserNotifications } from "../thunks.js";

const messages = {
  from: {
    id: "modules.UserNotification.components.NotificationsList.from",
    description: "Start date",
    defaultMessage: "From",
  },
  to: {
    id: "modules.UserNotification.components.NotificationsList.to",
    description: "End date",
    defaultMessage: "To",
  },
  message: {
    id: "modules.UserNotification.components.NotificationsList.message",
    description: "Notification content",
    defaultMessage: "Message",
  },
  noResults: {
    id: "modules.UserNotifications.components.NotificationsList.noResults",
    description: "Warning message no notifications fetched",
    defaultMessage: "Nothing found!",
  },
};

const UserNotifications = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);
  const pageSize = 10;

  const notifications = useSelector(selectNotifications);
  const isLoading = useSelector(isLoadingNotifications);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchUserNotifications({ page, pageSize }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const dismissError = () => setError(null);

  const mapUserNotificationToTableRow = (notificationMessage) => (
    <tr key={`message-${notificationMessage.id}`}>
      <td>
        <Link to={`/notifications/notification/edit/${notificationMessage.id}`}>
          {notificationMessage.notificationText}
        </Link>
      </td>
      <td>{moment(notificationMessage.startDate).format("LLL")}</td>
      <td>{moment(notificationMessage.endDate).format("LLL")}</td>
      <td>{moment(notificationMessage.updatedAt).format("LLL")}</td>
    </tr>
  );

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {formatMessage(genericTranslations.errorMessage)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      {isLoading && (
        <span className="align-middle">
          {formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && notifications.length === 0 && (
        <span className="align-middle">
          {formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {!isLoading && notifications.length > 0 && (
        <Table responsive>
          <thead>
            <tr>
              <td>{formatMessage(messages.message)}</td>
              <td>{formatMessage(messages.from)}</td>
              <td>{formatMessage(messages.to)}</td>
              <td>{formatMessage(genericTranslations.updatedAt)}</td>
            </tr>
          </thead>
          <tbody>{notifications.map(mapUserNotificationToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter
        actionPath="/notifications/notification/add"
        onSelectPage={onSelectPage}
        page={page}
        pages={pages}
      />
    </div>
  );
};

export default UserNotifications;
